// bpmnDragResolve — the PURE drop-target resolver for HORIZONTAL (BPMN) step
// drag. The horizontal analog of `resolveVerticalDrop` (processDragResolve.ts):
// the BPMN view lays its steps out left→right (grid columns), so the insertion
// anchor is picked by the pointer X against each candidate node's horizontal
// midpoint, not its Y. Kept pure + surface-agnostic so it is unit-tested with
// synthetic geometry and reused by the view, which supplies the node bands (from
// the composed BpmnShape geometry — NOT a DOM measurement) and calls
// `stepEdit.moveStep(dragged, anchor, position)` on drop.

/** One draggable BPMN step as a horizontal band in canvas space. */
export interface BpmnDragNode {
  readonly nodeId: string;
  /** Band left / width in the same space the pointer X is projected into. */
  readonly left: number;
  readonly width: number;
}

/** Where a drop lands: before/after a sibling anchor (the moveStep contract). */
export interface BpmnDropTarget {
  readonly anchorNodeId: string;
  readonly position: "before" | "after";
}

/**
 * Resolve a horizontal drag drop among ordered sibling nodes: the first node
 * whose MID-LINE sits to the RIGHT of the pointer means "insert before that
 * node"; past them all means "after the last". The dragged node itself is
 * excluded (it is the thing moving). Returns null when there is no OTHER node to
 * anchor to, or when the resolved target is a no-op (dropping back into the
 * dragged node's own slot) — the caller skips the mutation so an idle drag never
 * churns the text. Mirror of `resolveVerticalDrop`, x-for-y.
 */
export function resolveHorizontalDrop(
  nodes: readonly BpmnDragNode[],
  pointerX: number,
  draggedNodeId: string,
): BpmnDropTarget | null {
  const ordered = [...nodes].sort((a, b) => a.left - b.left);
  const draggedIdx = ordered.findIndex((n) => n.nodeId === draggedNodeId);
  const others = ordered.filter((n) => n.nodeId !== draggedNodeId);
  if (others.length === 0) return null;

  // The insertion index among `others` (0..others.length): first node whose
  // midpoint is to the right of the pointer.
  let insertAt = others.length;
  for (let i = 0; i < others.length; i++) {
    const n = others[i]!;
    if (pointerX < n.left + n.width / 2) {
      insertAt = i;
      break;
    }
  }

  // No-op guard: dropping into the dragged node's current slot. The nodes BEFORE
  // the dragged one (all non-dragged) number exactly `draggedIdx`, so in the
  // `others`-indexed space its original slot is `insertAt === draggedIdx`.
  if (draggedIdx !== -1 && insertAt === draggedIdx) {
    return null;
  }

  if (insertAt >= others.length) {
    return { anchorNodeId: others[others.length - 1]!.nodeId, position: "after" };
  }
  return { anchorNodeId: others[insertAt]!.nodeId, position: "before" };
}
